import {
  OPERATOR_USER_ROLES,
  TENANT_USER_ROLES,
  USER_ROLES,
  USER_ROLE_LABELS,
  USER_STATUSES,
  USER_STATUS_LABELS,
  type TenantSummary,
  type UserKind,
  type UserRole,
  type UserView,
} from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { temporaryPassword } from '../../passwords';
import { useSession } from '../../session';
import { Empty, ErrorBanner, Modal, SellerChip, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';

const ROLE_HINTS: Readonly<Record<UserRole, string>> = {
  owner: 'Firmanın her işini yapar, kullanıcı ekler ve yetki verir',
  manager: 'Sipariş, ürün, mal kabul ve belge işlerini yapar; kullanıcı yönetemez',
  viewer: 'Yalnız görüntüler',
  admin: 'Depo, müşteri, fiyat ve kullanıcı yönetimi',
  supervisor: 'Vardiya yönetimi, sorunlu sipariş kararları, istasyon kurulumu',
  staff: 'Mal kabul, yerleştirme, toplama, paketleme ve sevkiyat',
};

/** Admins: warehouse staff or the sellers' users; a seller's yönetici: the company's own users. */
export function AdminUsers() {
  const [kind, setKind] = useState<UserKind>('operator');
  const [tenantId, setTenantId] = useState('');
  const tenants = useLoad(() => api<TenantSummary[]>('GET', '/api/admin/tenants'), []);
  return (
    <>
      <div className="row">
        <div className="tabs" role="tablist">
          <button className={kind === 'operator' ? 'on' : ''} onClick={() => setKind('operator')} role="tab" aria-selected={kind === 'operator'}>
            Depo personeli
          </button>
          <button className={kind === 'tenant' ? 'on' : ''} onClick={() => setKind('tenant')} role="tab" aria-selected={kind === 'tenant'}>
            Müşteri kullanıcıları
          </button>
        </div>
        {kind === 'tenant' && (
          <select className="select" style={{ maxWidth: 260 }} value={tenantId} onChange={(e) => setTenantId(e.target.value)} aria-label="Müşteri">
            <option value="">Tüm müşteriler</option>
            {(tenants.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} · {t.displayName}
              </option>
            ))}
          </select>
        )}
      </div>
      <UsersPanel key={`${kind}|${tenantId}`} kind={kind} tenantId={tenantId || null} tenants={tenants.data ?? []} />
    </>
  );
}

export function SellerUsers() {
  return <UsersPanel kind="tenant" tenantId={null} tenants={[]} />;
}

interface PanelProps {
  readonly kind: UserKind;
  /** one seller's users; null = all the caller may see */
  readonly tenantId: string | null;
  readonly tenants: readonly TenantSummary[];
}

export function UsersPanel({ kind, tenantId, tenants }: PanelProps) {
  const session = useSession();
  const toast = useToast();
  const query = new URLSearchParams({ kind, ...(tenantId ? { tenantId } : {}) });
  const list = useLoad(() => api<UserView[]>('GET', `/api/users?${query}`), [kind, tenantId]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<UserView | 'new' | null>(null);
  const [resetting, setResetting] = useState<UserView | null>(null);
  const [handOver, setHandOver] = useState<{ user: UserView; password: string } | null>(null);
  const canManage = session.isAdmin || (!session.isOperator && session.me.role === 'owner');
  const companies = kind === 'tenant' && !tenantId && session.isOperator;
  const sort = useSort<UserView>('users', {
    name: { label: 'Ad soyad', value: (u) => u.fullName },
    ...(companies ? { company: { label: 'Firma', value: (u) => u.tenantCode } } : {}),
    // the roles from the most rights down, not their labels' alphabet
    role: { label: 'Rol', value: (u) => USER_ROLES.indexOf(u.role) },
    status: { label: 'Durum', value: (u) => USER_STATUSES.indexOf(u.status) },
    lastLogin: { label: 'Son giriş', value: (u) => u.lastLoginAt, desc: true },
  });
  const term = search.trim().toLocaleLowerCase('tr');
  const users = sort.apply((list.data ?? []).filter((u) => !term || `${u.fullName} ${u.email} ${u.tenantCode ?? ''}`.toLocaleLowerCase('tr').includes(term)));

  // a lost phone: the user sets two-factor sign-in up again at the next login
  const resetTwoFactor = async (u: UserView) => {
    if (!window.confirm(`${u.fullName} için iki adımlı doğrulama sıfırlansın mı? Bir sonraki girişte yeniden kurması gerekir.`)) return;
    try {
      await api('POST', `/api/users/${u.id}/two-factor/reset`);
      toast(`${u.fullName}: iki adımlı doğrulama sıfırlandı`, 'ok');
      void list.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const toggle = async (u: UserView) => {
    try {
      await api('PATCH', `/api/users/${u.id}`, { status: u.status === 'disabled' ? 'active' : 'disabled' });
      toast(u.status === 'disabled' ? `${u.fullName} yeniden aktif` : `${u.fullName} pasife alındı; oturumu kapatıldı`, 'ok');
      void list.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  return (
    <section className="card">
      <div className="card-h">
        <h2>{kind === 'operator' ? 'Depo personeli' : 'Kullanıcılar'}</h2>
        <span className="sub">{list.data ? `${users.length} kişi` : ''}</span>
        <span className="spacer" />
        {sort.picker}
        <input className="input" style={{ maxWidth: 220 }} placeholder="Ad veya e-posta ara" value={search} onChange={(e) => setSearch(e.target.value)} />
        {canManage && (
          <button className="btn primary sm" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} /> Yeni kullanıcı
          </button>
        )}
      </div>
      {handOver && (
        <div className="banner ok" style={{ margin: 12 }}>
          <Icon name="key" />
          <div style={{ flex: 1 }}>
            <b>{handOver.user.fullName}</b> için geçici şifre: <span className="mono big-code">{handOver.password}</span>
            <div style={{ fontWeight: 500 }}>Bu şifre bir daha gösterilmez; kullanıcıya iletin. İlk girişte kendi şifresini belirlemesi istenecek.</div>
          </div>
          <button className="btn sm" onClick={() => void navigator.clipboard?.writeText(handOver.password).then(() => toast('Şifre kopyalandı', 'ok'))}>
            Kopyala
          </button>
          <button className="icon-btn" onClick={() => setHandOver(null)} aria-label="Kapat">
            <Icon name="x" />
          </button>
        </div>
      )}
      {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
      {!list.data ? (
        <Spinner />
      ) : users.length === 0 ? (
        <Empty title="Kullanıcı yok">{canManage ? '"Yeni kullanıcı" ile ekleyin.' : null}</Empty>
      ) : (
        <div className="table-wrap">
          <table className="t stack-sm">
            <thead>
              <tr>
                {sort.th('name')}
                {companies && sort.th('company')}
                {sort.th('role')}
                {sort.th('status')}
                {sort.th('lastLogin')}
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={u.status === 'disabled' ? 'muted-row' : ''}>
                  <td>
                    <b>{u.fullName}</b>
                    {u.id === session.me.id && <span className="chip info" style={{ marginLeft: 6 }}>Siz</span>}
                    <div className="muted mono" style={{ fontSize: 13 }}>
                      {u.email}
                    </div>
                  </td>
                  {companies && <td>{u.tenantCode && <SellerChip code={u.tenantCode} />}</td>}
                  <td>{USER_ROLE_LABELS[u.role]}</td>
                  <td>
                    <span className={`chip ${u.status === 'active' ? 'ok' : u.status === 'disabled' ? '' : 'info'}`}>{USER_STATUS_LABELS[u.status]}</span>
                    {u.mustChangePassword && u.status === 'active' && <span className="chip warn" style={{ marginLeft: 6 }}>Geçici şifre</span>}
                    {u.twoFactorEnabled && (
                      <span className="chip ok" style={{ marginLeft: 6 }} title="Girişte doğrulama uygulamasının kodunu da soruyor">
                        2 adımlı
                      </span>
                    )}
                  </td>
                  <td className="muted" data-label="Son giriş">{u.lastLoginAt ? fmt.dateTime(u.lastLoginAt) : 'Hiç girmedi'}</td>
                  {canManage && (
                    <td className="nowrap" style={{ textAlign: 'right' }}>
                      {u.id !== session.me.id && (
                        <>
                          <button className="btn sm ghost" onClick={() => setEditing(u)} title="Ad ve rolü değiştir">
                            <Icon name="edit" size={16} />
                          </button>
                          <button className="btn sm ghost" onClick={() => setResetting(u)} title="Geçici şifre ver">
                            <Icon name="key" size={16} />
                          </button>
                          {u.twoFactorEnabled && (
                            <button className="btn sm ghost" onClick={() => void resetTwoFactor(u)} title="Telefonunu kaybeden kullanıcının iki adımlı doğrulamasını sıfırla">
                              <Icon name="shield" size={16} />
                            </button>
                          )}
                          <button className="btn sm" onClick={() => void toggle(u)}>
                            {u.status === 'disabled' ? 'Aktifleştir' : 'Pasife al'}
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserDialog
          kind={kind}
          tenantId={tenantId}
          tenants={tenants}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(user, password) => {
            setEditing(null);
            if (password) setHandOver({ user, password });
            else toast('Kullanıcı güncellendi', 'ok');
            void list.reload();
          }}
        />
      )}
      {resetting && (
        <PasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={(password) => {
            setHandOver({ user: resetting, password });
            setResetting(null);
            void list.reload();
          }}
        />
      )}
    </section>
  );
}

function UserDialog(props: {
  kind: UserKind;
  tenantId: string | null;
  tenants: readonly TenantSummary[];
  existing: UserView | null;
  onClose: () => void;
  onSaved: (user: UserView, password: string | null) => void;
}) {
  const session = useSession();
  const roles = props.kind === 'operator' ? OPERATOR_USER_ROLES : TENANT_USER_ROLES;
  const [fullName, setFullName] = useState(props.existing?.fullName ?? '');
  const [email, setEmail] = useState(props.existing?.email ?? '');
  const [role, setRole] = useState<UserRole>(props.existing?.role ?? (props.kind === 'operator' ? 'staff' : 'manager'));
  const [tenantId, setTenantId] = useState(props.tenantId ?? '');
  const [password] = useState(temporaryPassword);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pickTenant = props.kind === 'tenant' && session.isOperator && !props.tenantId && !props.existing;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (props.existing) {
        props.onSaved(await api<UserView>('PATCH', `/api/users/${props.existing.id}`, { fullName, role }), null);
      } else {
        const created = await api<UserView>('POST', '/api/users', {
          kind: props.kind,
          tenantId: props.kind === 'tenant' ? tenantId || null : null,
          role,
          email,
          fullName,
          password,
        });
        props.onSaved(created, password);
      }
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={props.existing ? `${props.existing.fullName} · düzenle` : props.kind === 'operator' ? 'Yeni depo personeli' : 'Yeni kullanıcı'}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !fullName.trim() || !email.trim() || (pickTenant && !tenantId)} onClick={() => void save()}>
            {props.existing ? 'Kaydet' : 'Kullanıcıyı oluştur'}
          </button>
        </>
      }
    >
      <form className="stack" onSubmit={(e) => e.preventDefault()} autoComplete="off">
        {error && <div className="banner bad">{error}</div>}
        {pickTenant && (
          <div className="field">
            <label htmlFor="u-tenant">Müşteri firma</label>
            <select id="u-tenant" className="select" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              <option value="">Seçin</option>
              {props.tenants
                .filter((t) => t.status !== 'offboarded')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} · {t.displayName}
                  </option>
                ))}
            </select>
          </div>
        )}
        <div className="field">
          <label htmlFor="u-name">Ad soyad</label>
          <input id="u-name" className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="u-email">E-posta</label>
          <input id="u-email" className="input" type="email" value={email} disabled={Boolean(props.existing)} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label>Rol</label>
          <div className="choice-list">
            {roles.map((r) => (
              <label key={r} className={`choice${role === r ? ' on' : ''}`}>
                <input type="radio" name="role" checked={role === r} onChange={() => setRole(r)} />
                <span>
                  <b>{USER_ROLE_LABELS[r]}</b>
                  <small>{ROLE_HINTS[r]}</small>
                </span>
              </label>
            ))}
          </div>
        </div>
        {!props.existing && (
          <p className="muted" style={{ fontSize: 13 }}>
            Geçici bir şifre oluşturulur ve kayıttan sonra bir kez gösterilir; kullanıcı ilk girişte kendi şifresini belirler.
          </p>
        )}
      </form>
    </Modal>
  );
}

function PasswordDialog({ user, onClose, onDone }: { user: UserView; onClose: () => void; onDone: (password: string) => void }) {
  const [password, setPassword] = useState(temporaryPassword);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api('POST', `/api/users/${user.id}/password`, { password });
      onDone(password);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`${user.fullName} · geçici şifre`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy} onClick={() => void save()}>
            Şifreyi ver
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <p>
          Kullanıcının şifresi aşağıdaki geçici şifreyle değiştirilir. Açık oturumları da dahil, kendi şifresini belirleyene kadar başka bir
          işlem yapamaz.
        </p>
        <div className="row">
          <span className="mono big-code">{password}</span>
          <button className="btn sm" onClick={() => setPassword(temporaryPassword())}>
            <Icon name="refresh" size={16} /> Yenile
          </button>
        </div>
      </div>
    </Modal>
  );
}
